import { Fragment, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { createPortal, flushSync } from "react-dom";
import { Link, useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { compareAdminEventRows } from "../lib/event-sort";
import { adminEventsListModeTitle, adminRowMatchesEventsListMode } from "./admin-events-mode";
import { dedupeJpRoomRowsForAdminList } from "../lib/jp-room-admin-dedupe";
import {
  adminListFilterMatchesRowWithVis,
  adminRowPassesDefaultEventDateFloor,
  parseAdminFocusDay,
  type AdminOutletFilter,
} from "./admin-list-filter";
import { ADMIN_EVENTS_LIST_SCROLL_ID, scrollToAdminListRow } from "./admin-scroll-focus";
import { parseEventFieldsFromText } from "./parseEventText";
import { apiJson } from "./api";
import {
  ADMIN_VISITOR_PATCH_STORAGE_KEY,
  inferAdminModeForRow,
  mergePrefillFromSearchParams,
  performersParamToComma,
  stripAdminEventPrefillKeys,
} from "./admin-event-prefill";
import { useAdminEventsData } from "./admin-events-data-context";
import {
  AdminPerformersPicker,
  adminPerformersSelectionFromRow,
  emptyAdminPerformersSelection,
  type AdminPerformersSelection,
} from "./AdminPerformersPicker";
import { KARIN_ICON_OPTIONS } from "../types/public";

type Row = Record<string, unknown>;

function parseList(v: unknown): string[] {
  if (typeof v !== "string") return [];
  try {
    const j = JSON.parse(v);
    return Array.isArray(j) ? j.map(String) : [];
  } catch {
    return [];
  }
}

function todayJp(): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}

function rowEventDateIsoDate(r: Row): string {
  const raw = String(r.event_date ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : "";
}

function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

function weeklyRepeatDates(start: string, until: string): string[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(until) || until < start) return [];
  const out: string[] = [];
  let cur = start;
  while (cur <= until && out.length < 60) {
    out.push(cur);
    cur = addDaysYmd(cur, 7);
  }
  return out;
}

const emptyForm: Row = {
  id: "",
  source: "manual",
  visibility: "public",
  event_kind: "event",
  event_name: "",
  venue: "",
  prefecture: "",
  event_date: todayJp(),
  time_start: "",
  time_end: "",
  door_time: "",
  curtain_time: "",
  door_time_2: "",
  curtain_time_2: "",
  door_time_3: "",
  curtain_time_3: "",
  homepage_public_date: "",
  homepage_public_at: "",
  performance_text: "",
  reception_period_text: "",
  performers: "[]",
  description: "",
  detail_url: "",
  reserve_url: "",
  memo: "",
  fan_group_name: "",
  fan_artist_name: "",
  source_title: "",
  fan_private_note: "",
  performance_count: "",
  is_karin: 0,
  karin_subtype: "other",
  karin_icon: "",
  group_code: "",
  reception_start: "",
  reception_end: "",
  status: "ok",
};

const JAPAN_PREFECTURES = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県", "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県", "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県", "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県",
];

function toDatetimeLocalInputValue(v: unknown): string {
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/);
  if (m) return `${m[1]}T${m[2]}:${m[3]}`;
  return "";
}

function formatHomepagePublicCell(r: Row): string {
  const date = String(r.homepage_public_date ?? "").trim();
  const atRaw = String(r.homepage_public_at ?? "").trim();
  const atLocal = toDatetimeLocalInputValue(atRaw);
  const timePart = atLocal.includes("T") ? atLocal.slice(11) : "";
  if (date && timePart) return `${date} ${timePart}`;
  if (date) return date;
  if (timePart) {
    const d = atLocal.slice(0, 10);
    return d && d !== "1970-01-01" ? `${d} ${timePart}` : timePart;
  }
  if (atRaw) return atRaw.length > 24 ? `${atRaw.slice(0, 24)}…` : atRaw;
  return "";
}

function previewCell(v: unknown, max = 44): string {
  const s = String(v ?? "").replace(/\s+/g, " ").trim();
  if (!s) return "—";
  return s.length <= max ? s : `${s.slice(0, max)}…`;
}

/** 本番欄: performance_text が空でも event_date・開場開演があれば短く表示 */
function schedulePreviewCell(r: Row): string {
  const perf = r.performance_text ?? r.time_start ?? r.curtain_time;
  if (perf != null && String(perf).trim() !== "") return String(perf);
  const date = String(r.event_date ?? "").trim();
  const door = String(r.door_time ?? "").trim();
  const cur = String(r.curtain_time ?? "").trim();
  const bits: string[] = [];
  if (date) bits.push(date);
  if (door) bits.push(`開場${door}`);
  if (cur) bits.push(`開演${cur}`);
  return bits.join(" ");
}

function isArchivedRow(r: Row): boolean {
  const v = r.deleted_at;
  if (v == null) return false;
  return String(v).trim().length > 0;
}

/** API / D1 からの visibility を正規化（比較用） */
function adminVisibilityKey(v: unknown): "public" | "unreleased" | "other" {
  const s = String(v ?? "").trim().toLowerCase();
  if (s === "public") return "public";
  if (s === "unreleased") return "unreleased";
  return "other";
}

/** 表サイト用 JSON に載るか（visibility）を一覧で識別しやすくする */
function visibilityUi(v: unknown): { rowClass: string; badgeClass: string; label: string; raw: string } {
  const raw = String(v ?? "").trim();
  const k = adminVisibilityKey(v);
  if (k === "public") {
    return { rowClass: "admin-row-vis-public", badgeClass: "admin-vis-badge admin-vis-public", label: "表に表示", raw };
  }
  if (k === "unreleased") {
    return { rowClass: "admin-row-vis-unreleased", badgeClass: "admin-vis-badge admin-vis-unreleased", label: "非公開（表に出さない）", raw };
  }
  return { rowClass: "admin-row-vis-unknown", badgeClass: "admin-vis-badge admin-vis-unknown", label: raw ? `その他: ${raw}` : "未設定", raw };
}

const TIME_HOURS = Array.from({ length: 30 }, (_, i) => String(i).padStart(2, "0"));
const TIME_MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

function splitTimeValue(value: string): { hour: string; minute: string } {
  const m = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return { hour: "", minute: "" };
  return { hour: m[1]!.padStart(2, "0"), minute: m[2]! };
}

function TimePickerField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const current = splitTimeValue(value);
  const setPart = (part: "hour" | "minute", next: string) => {
    const hour = part === "hour" ? next : current.hour;
    const minute = part === "minute" ? next : current.minute;
    if (!hour && !minute) return onChange("");
    onChange(`${hour || "00"}:${minute || "00"}`);
  };
  return (
    <div className="admin-time-picker-field">
      <div className="admin-time-picker-label">{label}</div>
      <div className="admin-time-picker-control">
        <select className="input admin-time-picker-select" value={current.hour} onChange={(e) => setPart("hour", e.target.value)}>
          <option value="">時</option>
          {TIME_HOURS.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <span className="admin-time-picker-colon">:</span>
        <select className="input admin-time-picker-select" value={current.minute} onChange={(e) => setPart("minute", e.target.value)}>
          <option value="">分</option>
          {TIME_MINUTES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <button type="button" className="btn secondary admin-time-picker-clear" onClick={() => onChange("")}>
          クリア
        </button>
      </div>
    </div>
  );
}

function InlineEventForm(props: {
  creating: boolean;
  editing: Row;
  setEditing: Dispatch<SetStateAction<Row | null>>;
  perfSelection: AdminPerformersSelection;
  setPerfSelection: Dispatch<SetStateAction<AdminPerformersSelection>>;
  weeklyRepeat: { enabled: boolean; until: string };
  setWeeklyRepeat: Dispatch<SetStateAction<{ enabled: boolean; until: string }>>;
  duplicateDate: string;
  setDuplicateDate: Dispatch<SetStateAction<string>>;
  saveBusy: boolean;
  saveFeedback: string | null;
  onDuplicateDate: () => void;
  onSaveClose: () => void;
  onSaveKeep: () => void;
  onCancel: () => void;
}) {
  const {
    creating,
    editing,
    setEditing,
    perfSelection,
    setPerfSelection,
    weeklyRepeat,
    setWeeklyRepeat,
    duplicateDate,
    setDuplicateDate,
    saveBusy,
    saveFeedback,
    onDuplicateDate,
    onSaveClose,
    onSaveKeep,
    onCancel,
  } = props;
  const [pasteText, setPasteText] = useState("");
  const [importHint, setImportHint] = useState<string | null>(null);
  const [ocrBusy, setOcrBusy] = useState(false);
  const repeatDates = weeklyRepeat.enabled ? weeklyRepeatDates(String(editing.event_date ?? ""), weeklyRepeat.until) : [];
  const repeatCreateCount = creating ? repeatDates.length : Math.max(0, repeatDates.length - 1);

  const applyFromParsed = (parsed: ReturnType<typeof parseEventFieldsFromText>) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const next: Row = { ...prev };
      for (const [k, v] of Object.entries(parsed.patch)) {
        next[k] = v;
      }
      return next;
    });
    if (parsed.perfNames.length) {
      setPerfSelection((prev) => {
        const uniq = new Set(prev.persons);
        for (const n of parsed.perfNames) uniq.add(n);
        return { ...prev, persons: [...uniq] };
      });
    }
  };

  const applyTextImport = () => {
    const t = pasteText.trim();
    if (!t) {
      setImportHint("テキストを貼り付けてから押してください。");
      return;
    }
    const parsed = parseEventFieldsFromText(t);
    applyFromParsed(parsed);
    const n = Object.keys(parsed.patch).length;
    setImportHint(`フォームに反映しました（${n}項目・出演者候補 ${parsed.perfNames.length} 名）。必ず目で確認してください。`);
  };

  const onPickImage = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setOcrBusy(true);
    setImportHint("画像を読み取り中です…（数十秒かかることがあります）");
    try {
      const { createWorker } = await import("tesseract.js");
      const worker = await createWorker("jpn+eng");
      try {
        const { data } = await worker.recognize(file);
        const t = data.text.trim();
        setPasteText(t);
        setImportHint("文字を認識しました。内容を確認してから「フォームに反映」を押してください。");
      } finally {
        await worker.terminate();
      }
    } catch (e) {
      setImportHint(`読み取りに失敗しました: ${String(e)}`);
    } finally {
      setOcrBusy(false);
    }
  };

  return (
    <div className="table-inline-form">
      <div className="row" style={{ justifyContent: "flex-end", marginBottom: 8 }}>
        <button className="btn small secondary" type="button" onClick={onCancel}>
          閉じる
        </button>
      </div>
      <h3 id="admin-event-modal-title" className="table-inline-form-title">
        {creating ? "新規作成" : "編集"}
      </h3>
      <div
        className="event-import-assist"
        style={{
          marginBottom: 14,
          padding: 12,
          borderRadius: 10,
          border: "1px solid var(--line)",
          background: "linear-gradient(180deg, #faf8ff, #fff)",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>テキスト・画像から自動読み込み</div>
        <p style={{ fontSize: 11, color: "var(--muted)", margin: "0 0 8px", lineHeight: 1.55 }}>
          公式の文面やチラシの文章を貼るか、画像を選んでブラウザ内 OCR します。推測なので必ず確認・修正してから保存してください。
        </p>
        <textarea
          className="input"
          rows={5}
          placeholder="ここに貼り付け…（例：イベント名、2026-05-14、開場17:30 開演18:00、会場：○○、https://…）"
          value={pasteText}
          disabled={ocrBusy}
          onChange={(e) => setPasteText(e.target.value)}
        />
        <div className="row" style={{ marginTop: 8, gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button className="btn small" type="button" disabled={ocrBusy} onClick={applyTextImport}>
            フォームに反映
          </button>
          <label className="btn small secondary" style={{ cursor: ocrBusy ? "wait" : "pointer", margin: 0 }}>
            <input type="file" accept="image/*" style={{ display: "none" }} disabled={ocrBusy} onChange={(e) => void onPickImage(e)} />
            画像を選んで OCR
          </label>
          {ocrBusy ? <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>処理中…</span> : null}
        </div>
        {importHint ? (
          <p className="mono" style={{ fontSize: 12, margin: "8px 0 0", color: "var(--muted)" }}>
            {importHint}
          </p>
        ) : null}
      </div>
      <div className="grid2">
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>取り込み元</div>
          <select className="input" value={String(editing.source)} onChange={(e) => setEditing({ ...editing, source: e.target.value })}>
            <option value="manual">手入力（manual）</option>
            <option value="helloproject">ハロー！プロジェクト（helloproject）</option>
            <option value="jp_room">M-line J.P ROOM（jp_room）</option>
            <option value="mline">M-line（mline）</option>
          </select>
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>公開範囲</div>
          <select className="input" value={String(editing.visibility)} onChange={(e) => setEditing({ ...editing, visibility: e.target.value })}>
            <option value="public">公開（表サイトに載せられる）</option>
            <option value="unreleased">未解禁（表には出さない）</option>
          </select>
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>解禁日時（未解禁のとき：この時刻を過ぎると自動で表サイト公開）</div>
          <input
            className="input"
            type="datetime-local"
            disabled={String(editing.visibility) !== "unreleased"}
            value={toDatetimeLocalInputValue(editing.homepage_public_at)}
            onChange={(e) => {
              const v = e.target.value;
              setEditing((prev) => {
                if (!prev) return prev;
                const next: Row = { ...prev, homepage_public_at: v };
                if (v && !String(prev.homepage_public_date ?? "").trim()) {
                  next.homepage_public_date = v.slice(0, 10);
                }
                return next;
              });
            }}
          />
          <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--muted)", lineHeight: 1.5 }}>
            サーバーは<strong>日本時間（JST）</strong>の「今」と比較します（約10分ごと）。日時ピッカーはブラウザのローカル時刻で保存されるため、日本国外ではずれることがあります。
          </p>
          {String(editing.visibility) !== "unreleased" ? (
            <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--muted)" }}>公開中は解禁予約は無効です。未解禁に戻すと再度有効になります。</p>
          ) : null}
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>イベント名</div>
          <input className="input" value={String(editing.event_name ?? "")} onChange={(e) => setEditing({ ...editing, event_name: e.target.value })} />
        </label>
        <div>
          <div className="admin-event-date-label-row">
            <label htmlFor="admin-event-date-input" style={{ fontSize: 12, color: "var(--muted)" }}>
              開催日（並び・表サイトの基準日）
            </label>
            <button
              className={`btn small secondary admin-weekly-repeat-toggle${weeklyRepeat.enabled ? " admin-weekly-repeat-toggle--on" : ""}`}
              type="button"
              aria-pressed={weeklyRepeat.enabled}
              onClick={() => setWeeklyRepeat((prev) => ({ ...prev, enabled: !prev.enabled }))}
            >
              {weeklyRepeat.enabled ? "毎週繰り返しON" : "毎週繰り返し"}
            </button>
          </div>
          <input id="admin-event-date-input" className="input" type="date" value={String(editing.event_date ?? "")} onChange={(e) => setEditing({ ...editing, event_date: e.target.value })} />
          {weeklyRepeat.enabled ? (
            <p className="admin-weekly-repeat-date-hint">
              {creating
                ? "下の「繰り返し終了日」を入れて保存すると、開催日から毎週分をまとめて作成します。"
                : "下の「繰り返し終了日」を入れて保存すると、この予定を保存し、翌週以降の分を追加作成します。"}
            </p>
          ) : null}
        </div>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>ホームページ公開日（一覧に載せ始める日）</div>
          <input className="input" type="date" value={String(editing.homepage_public_date ?? "")} onChange={(e) => setEditing({ ...editing, homepage_public_date: e.target.value })} />
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>予定の種類</div>
          <select className="input" value={String(editing.event_kind ?? "event")} onChange={(e) => setEditing({ ...editing, event_kind: e.target.value })}>
            <option value="live">ライブ（live）</option>
            <option value="concert">コンサート（concert）</option>
            <option value="event">イベント（event）</option>
            <option value="stage">舞台（stage）</option>
            <option value="musical">ミュージカル（musical）</option>
            <option value="release_event">発売イベント（release_event）</option>
            <option value="sign">サイン会（sign）</option>
            <option value="talk">トーク（talk）</option>
            <option value="public_recording">公開録音（public_recording）</option>
            <option value="stream">配信・中継（stream）</option>
            <option value="radio">ラジオ（radio）</option>
            <option value="tv">テレビ（tv）</option>
            <option value="magazine">雑誌・書籍（magazine）</option>
            <option value="web">WEB（web）</option>
          </select>
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>会場名</div>
          <input className="input" value={String(editing.venue ?? "")} onChange={(e) => setEditing({ ...editing, venue: e.target.value })} />
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>都道府県</div>
          <select className="input" value={String(editing.prefecture ?? "")} onChange={(e) => setEditing({ ...editing, prefecture: e.target.value })}>
            <option value="">未設定</option>
            {JAPAN_PREFECTURES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>ファン投稿: グループ名</div>
          <input className="input" value={String(editing.fan_group_name ?? "")} onChange={(e) => setEditing({ ...editing, fan_group_name: e.target.value })} />
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>ファン投稿: アーティスト名</div>
          <input className="input" value={String(editing.fan_artist_name ?? "")} onChange={(e) => setEditing({ ...editing, fan_artist_name: e.target.value })} />
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>公演数</div>
          <input className="input" type="number" min={1} max={99} value={String(editing.performance_count ?? "")} onChange={(e) => setEditing({ ...editing, performance_count: e.target.value })} />
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>URL取得タイトル</div>
          <input className="input" value={String(editing.source_title ?? "")} onChange={(e) => setEditing({ ...editing, source_title: e.target.value })} />
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>ファン投稿: 補足・出典メモ（管理のみ）</div>
          <textarea className="input" rows={2} value={String(editing.fan_private_note ?? "")} onChange={(e) => setEditing({ ...editing, fan_private_note: e.target.value })} />
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>宮本佳林の予定</div>
          <select className="input" value={String(Number(editing.is_karin))} onChange={(e) => setEditing({ ...editing, is_karin: Number(e.target.value) })}>
            <option value={0}>いいえ（0）</option>
            <option value={1}>はい（1）</option>
          </select>
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>佳林予定のタイプ</div>
          <select className="input" value={String(editing.karin_subtype ?? "other")} onChange={(e) => setEditing({ ...editing, karin_subtype: e.target.value })}>
            <option value="mini_live">ミニライブ＆一言（mini_live）</option>
            <option value="withlive">WithLIVE（withlive）</option>
            <option value="limista">リミスタ（limista）</option>
            <option value="live">ライブ（live）</option>
            <option value="m_line">M-line（m_line）</option>
            <option value="other">その他（other）</option>
          </select>
        </label>
        {Number(editing.is_karin) ? (
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 6 }}>表サイトの佳林アイコン</div>
            <div className="admin-karin-icon-picker" role="radiogroup" aria-label="表サイトの佳林アイコン">
              {KARIN_ICON_OPTIONS.map((opt) => {
                const selected = String(editing.karin_icon ?? "") === opt.value;
                return (
                  <button
                    key={opt.value || "default"}
                    className={`admin-karin-icon-option${selected ? " admin-karin-icon-option--selected" : ""}`}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    title={opt.label}
                    onClick={() => setEditing({ ...editing, karin_icon: opt.value })}
                  >
                    <span className="admin-karin-icon-option-mark">{opt.emoji}</span>
                    <span className="admin-karin-icon-option-label">{opt.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="meta" style={{ margin: "6px 0 0", fontSize: 11 }}>
              選ばずに保存した予定は、今まで通り予定の種類に合う顔アイコンになります。後から編集で変更できます。
            </p>
          </div>
        ) : null}
        <TimePickerField label="開始時刻" value={String(editing.time_start ?? "")} onChange={(v) => setEditing({ ...editing, time_start: v })} />
        <TimePickerField label="終了時刻" value={String(editing.time_end ?? "")} onChange={(v) => setEditing({ ...editing, time_end: v })} />
        {([1, 2, 3] as const).map((n) => {
          const dk = n === 1 ? "door_time" : n === 2 ? "door_time_2" : "door_time_3";
          const ck = n === 1 ? "curtain_time" : n === 2 ? "curtain_time_2" : "curtain_time_3";
          return (
            <Fragment key={n}>
              <TimePickerField label={`第${n}公演 開場`} value={String(editing[dk] ?? "")} onChange={(v) => setEditing({ ...editing, [dk]: v })} />
              <TimePickerField label={`第${n}公演 開演`} value={String(editing[ck] ?? "")} onChange={(v) => setEditing({ ...editing, [ck]: v })} />
            </Fragment>
          );
        })}
        <label style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>本番日程・公演時間（メモ）</div>
          <textarea className="input" rows={4} value={String(editing.performance_text ?? "")} onChange={(e) => setEditing({ ...editing, performance_text: e.target.value })} />
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>予約・発売（メモ）</div>
          <textarea className="input" rows={3} value={String(editing.reception_period_text ?? "")} onChange={(e) => setEditing({ ...editing, reception_period_text: e.target.value })} />
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>受付開始日</div>
          <input className="input" type="date" value={String(editing.reception_start ?? "")} onChange={(e) => setEditing({ ...editing, reception_start: e.target.value })} />
        </label>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>受付終了日</div>
          <input className="input" type="date" value={String(editing.reception_end ?? "")} onChange={(e) => setEditing({ ...editing, reception_end: e.target.value })} />
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>公式詳細URL</div>
          <input className="input" value={String(editing.detail_url ?? "")} onChange={(e) => setEditing({ ...editing, detail_url: e.target.value })} />
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>予約・申込URL</div>
          <input className="input" value={String(editing.reserve_url ?? "")} onChange={(e) => setEditing({ ...editing, reserve_url: e.target.value })} />
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>説明文</div>
          <textarea className="input" rows={3} value={String(editing.description ?? "")} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>管理メモ</div>
          <textarea className="input" rows={2} value={String(editing.memo ?? "")} onChange={(e) => setEditing({ ...editing, memo: e.target.value })} />
        </label>
        <div style={{ gridColumn: "1 / -1" }}>
          <AdminPerformersPicker value={perfSelection} onChange={setPerfSelection} />
        </div>
        <label>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>状態</div>
          <select className="input" value={String(editing.status ?? "ok")} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
            <option value="ok">確定（ok）</option>
            <option value="pending_review">要確認（pending_review）</option>
          </select>
        </label>
      </div>
      {(creating || editing) ? (
        <section className="card" style={{ marginTop: 12, padding: 12, background: "rgba(250,245,255,0.72)" }}>
          <label className="row" style={{ gap: 8, alignItems: "center", marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={weeklyRepeat.enabled}
              onChange={(e) => setWeeklyRepeat((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            <strong>毎週繰り返す予定として登録</strong>
          </label>
          <p className="meta" style={{ margin: "0 0 8px", fontSize: 12, lineHeight: 1.55 }}>
            {creating
              ? "開催日を1回目として、終了日まで1週間ごとに同じ内容で登録します。最大60件までです。"
              : "編集中の予定を保存し、翌週以降を同じ内容で追加登録します。既存の開催日は重複登録しません。最大60件までです。"}
          </p>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 220 }}>
            <span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>繰り返し終了日</span>
            <input
              className="input"
              type="date"
              disabled={!weeklyRepeat.enabled}
              value={weeklyRepeat.until}
              min={String(editing.event_date ?? "")}
              onChange={(e) => setWeeklyRepeat((prev) => ({ ...prev, until: e.target.value }))}
            />
          </label>
          {weeklyRepeat.enabled ? (
            <p className="mono" style={{ margin: "8px 0 0", fontSize: 12, color: repeatDates.length ? "var(--muted)" : "#b00020" }}>
              {repeatCreateCount
                ? `${repeatCreateCount}件作成予定（${creating ? repeatDates[0] : repeatDates[1]}〜${repeatDates[repeatDates.length - 1]}）`
                : creating
                  ? "開催日と終了日を確認してください"
                  : "翌週以降も含む終了日を指定してください"}
            </p>
          ) : null}
        </section>
      ) : null}
      {!creating ? (
        <section className="card admin-duplicate-date-card">
          <div className="admin-duplicate-date-head">
            <strong>この予定を別日に追加</strong>
            <span>現在の入力内容をコピーして、指定した開催日で新しい予定を作ります。</span>
          </div>
          <div className="row admin-duplicate-date-row">
            <label>
              <span className="mono">追加する開催日</span>
              <input
                className="input"
                type="date"
                value={duplicateDate}
                onChange={(e) => setDuplicateDate(e.target.value)}
              />
            </label>
            <button className="btn secondary" type="button" disabled={saveBusy || !duplicateDate} onClick={onDuplicateDate}>
              {saveBusy ? "追加中…" : "別日に追加"}
            </button>
          </div>
          <p className="meta">元の予定はこのボタンでは変更しません。元の予定も直す場合は「保存して閉じる」または「内容を保持して保存」を押してください。</p>
        </section>
      ) : null}
      <div className="row" style={{ marginTop: 12, flexWrap: "wrap", gap: 8 }}>
        <button className="btn" type="button" disabled={saveBusy} onClick={onSaveClose}>
          {saveBusy ? "保存中…" : "保存して閉じる"}
        </button>
        <button className="btn secondary" type="button" disabled={saveBusy} onClick={onSaveKeep}>
          {saveBusy ? "保存中…" : "内容を保持して保存"}
        </button>
        <button className="btn secondary" type="button" disabled={saveBusy} onClick={onCancel}>
          キャンセル
        </button>
      </div>
      {saveFeedback ? (
        <p className="admin-event-form-status" role="status" aria-live="polite">
          {saveFeedback}
        </p>
      ) : null}
      <p className="mono" style={{ marginTop: 8, fontSize: 11, color: "var(--muted)", lineHeight: 1.5 }}>
        「内容を保持して保存」は同じイベント名のまま日付・会場だけ変えて別公演を続けて登録するときに使います。新規の直後は同じ内容の新規作成モードのまま残ります。
      </p>
    </div>
  );
}

export function EventsPage() {
  const { mode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const adminFocusDay = parseAdminFocusDay(searchParams.get("focus_day"));
  const prefillConsumedRef = useRef(false);
  const adminCtx = useOutletContext<AdminOutletFilter | undefined>();
  const adminEventDateFrom = adminCtx?.adminEventDateFrom ?? "";
  const adminEventDateTo = adminCtx?.adminEventDateTo ?? "";
  const adminKeyword = adminCtx?.adminKeyword ?? "";
  const adminKarinSubtype = adminCtx?.adminKarinSubtype ?? "all";
  const adminVis = adminCtx?.adminVis ?? "";
  const { rows, setRows, reload, fetchError } = useAdminEventsData();
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);
  const [creating, setCreating] = useState(false);
  const [weeklyRepeat, setWeeklyRepeat] = useState<{ enabled: boolean; until: string }>({ enabled: false, until: "" });
  const [duplicateDate, setDuplicateDate] = useState("");
  const [perfSelection, setPerfSelection] = useState<AdminPerformersSelection>(emptyAdminPerformersSelection);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [visibilityOverride, setVisibilityOverride] = useState<Record<string, "public" | "unreleased">>({});
  /** 公開・非公開：連打時は最新の意図だけサーバーへ追いつかせる（行ごと） */
  const visibilityDesiredRef = useRef<Record<string, "public" | "unreleased">>({});
  const visibilityDrainRunningRef = useRef(new Set<string>());
  /** 一覧で「非公開」の行だけチェック → まとめて公開 */
  const [bulkPubPick, setBulkPubPick] = useState<Record<string, boolean>>({});

  const title = useMemo(() => adminEventsListModeTitle(mode), [mode]);

  useEffect(() => {
    const editId = searchParams.get("edit")?.trim();
    const isNew = searchParams.get("new") === "1" || searchParams.get("prefill") === "1";
    if (!editId && !isNew) {
      prefillConsumedRef.current = false;
      return;
    }
    if (prefillConsumedRef.current) return;
    const snapshot = new URLSearchParams(searchParams);

    if (isNew) {
      prefillConsumedRef.current = true;
      const base: Row = {
        ...emptyForm,
        source: mode === "helloproject" ? "helloproject" : mode === "jp_room" ? "jp_room" : mode === "mline" ? "mline" : "manual",
        is_karin: mode === "karin" ? 1 : 0,
      };
      const merged = mergePrefillFromSearchParams(base, snapshot);
      const perf = performersParamToComma(snapshot);
      setCreating(true);
      setEditing(merged);
      setWeeklyRepeat({ enabled: false, until: "" });
      setDuplicateDate("");
      setPerfSelection({
        persons: perf ? perf.split(/[,、]/).map((s) => s.trim()).filter(Boolean) : [],
        groupCodes: merged.group_code ? [String(merged.group_code)] : [],
      });
      setSearchParams((prev) => stripAdminEventPrefillKeys(prev), { replace: true });
      return;
    }

    if (editId) {
      prefillConsumedRef.current = true;
      void (async () => {
        try {
          const row = await apiJson<Row>(`/api/admin/events/${encodeURIComponent(editId)}`);
          const wantMode = inferAdminModeForRow(row);
          if (wantMode !== mode) {
            prefillConsumedRef.current = false;
            navigate(`/admin/events/${wantMode}?edit=${encodeURIComponent(editId)}`, { replace: true });
            return;
          }
          setCreating(false);
          let merged: Row = { ...row };
          const raw = sessionStorage.getItem(ADMIN_VISITOR_PATCH_STORAGE_KEY);
          if (raw) {
            try {
              const parsed = JSON.parse(raw) as { event_id?: string; patch?: Record<string, string> };
              if (parsed.event_id === editId && parsed.patch && typeof parsed.patch === "object") {
                merged = { ...merged, ...parsed.patch };
              }
            } catch {
              /* ignore */
            } finally {
              sessionStorage.removeItem(ADMIN_VISITOR_PATCH_STORAGE_KEY);
            }
          }
          setEditing(merged);
          setDuplicateDate("");
          setPerfSelection(adminPerformersSelectionFromRow(merged.performers_json, merged.group_code, parseList));
          setSearchParams((prev) => stripAdminEventPrefillKeys(prev), { replace: true });
        } catch (e) {
          setErr(String(e));
          setSearchParams((prev) => stripAdminEventPrefillKeys(prev), { replace: true });
        }
      })();
    }
  }, [mode, navigate, searchParams, setSearchParams]);

  const rowsWithVisibilityOverride = useMemo(
    () =>
      rows.map((r) => {
        const id = String(r.id ?? "");
        const visibility = id ? visibilityOverride[id] : undefined;
        return visibility ? { ...r, visibility, status: visibility === "public" ? "ok" : r.status } : r;
      }),
    [rows, visibilityOverride],
  );

  const modeFiltered = useMemo(() => rowsWithVisibilityOverride.filter((r) => adminRowMatchesEventsListMode(r, mode)), [rowsWithVisibilityOverride, mode]);

  const filtered = useMemo(() => {
    return modeFiltered.filter(
      (r) => {
        const id = String(r.id ?? "");
        const visForFilter = mode === "archive" || (id && visibilityOverride[id]) ? "" : adminVis;
        return (
          adminRowPassesDefaultEventDateFloor(mode, adminEventDateFrom, adminEventDateTo, r) &&
          adminListFilterMatchesRowWithVis(adminEventDateFrom, adminEventDateTo, adminKeyword, visForFilter, r, adminKarinSubtype)
        );
      },
    );
  }, [modeFiltered, mode, adminEventDateFrom, adminEventDateTo, adminKeyword, adminVis, adminKarinSubtype, visibilityOverride]);

  /** J.P ROOM タブのみ: 同一 event/detail と同一公演枠の共有イベントをメンバー別レコードで複数行にしない */
  const filteredDeduped = useMemo(() => dedupeJpRoomRowsForAdminList(filtered, mode), [filtered, mode]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredDeduped];
    if (mode === "archive") {
      arr.sort((a, b) => {
        const da = String(a.deleted_at ?? "");
        const db = String(b.deleted_at ?? "");
        const t = db.localeCompare(da);
        if (t !== 0) return t;
        return compareAdminEventRows(a, b);
      });
      return arr;
    }
    arr.sort(compareAdminEventRows);
    return arr;
  }, [filteredDeduped, mode]);

  useEffect(() => {
    if (!adminFocusDay) return;
    if (!location.pathname.startsWith("/admin/events")) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToAdminListRow(adminFocusDay));
    });
  }, [adminFocusDay, location.pathname, sortedRows.length]);

  const unreleasedIdsInSorted = useMemo(
    () => sortedRows.filter((r) => adminVisibilityKey(r.visibility) === "unreleased").map((r) => String(r.id)),
    [sortedRows],
  );

  const unreleasedPickPruneKey = useMemo(() => [...unreleasedIdsInSorted].sort().join("|"), [unreleasedIdsInSorted]);

  useEffect(() => {
    const allow = new Set(unreleasedPickPruneKey.split("|").filter(Boolean));
    setBulkPubPick((prev) => {
      const next: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(prev)) {
        if (v && allow.has(k)) next[k] = true;
      }
      return next;
    });
  }, [unreleasedPickPruneKey]);

  const pickedUnreleasedCount = useMemo(
    () => unreleasedIdsInSorted.filter((id) => bulkPubPick[id]).length,
    [unreleasedIdsInSorted, bulkPubPick],
  );

  const allUnreleasedPicked = useMemo(
    () => unreleasedIdsInSorted.length > 0 && unreleasedIdsInSorted.every((id) => bulkPubPick[id]),
    [unreleasedIdsInSorted, bulkPubPick],
  );

  const bulkPickHeaderRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = bulkPickHeaderRef.current;
    if (!el) return;
    el.indeterminate = pickedUnreleasedCount > 0 && !allUnreleasedPicked;
  }, [pickedUnreleasedCount, allUnreleasedPicked]);

  const openEdit = (r: Row) => {
    setCreating(false);
    setSaveFeedback(null);
    setWeeklyRepeat({ enabled: false, until: "" });
    setDuplicateDate("");
    setEditing({ ...r });
    setPerfSelection(adminPerformersSelectionFromRow(r.performers_json, r.group_code, parseList));
  };

  const openCreate = () => {
    setCreating(true);
    setSaveFeedback(null);
    setEditing({ ...emptyForm, source: mode === "helloproject" ? "helloproject" : mode === "jp_room" ? "jp_room" : mode === "mline" ? "mline" : "manual", is_karin: mode === "karin" ? 1 : 0 });
    setWeeklyRepeat({ enabled: false, until: "" });
    setDuplicateDate("");
    setPerfSelection(emptyAdminPerformersSelection());
  };

  const buildSaveBody = (): Record<string, unknown> | null => {
    if (!editing) return null;
    const at = String(editing.homepage_public_at ?? "").trim();
    let homepage_public_date = String(editing.homepage_public_date ?? "").trim();
    if (at && !homepage_public_date) homepage_public_date = at.slice(0, 10);
    return {
      ...editing,
      homepage_public_date: homepage_public_date || null,
      homepage_public_at: at || null,
      performers_json: JSON.stringify(perfSelection.persons),
      group_code: perfSelection.groupCodes[0] ?? "",
    };
  };

  const save = async (keepOpen: boolean) => {
    if (saveBusy) return;
    const body = buildSaveBody();
    if (!body) return;
    const wasCreating = creating;
    setSaveBusy(true);
    setSaveFeedback(keepOpen ? "保存中です…" : null);
    if (!keepOpen) {
      setEditing(null);
      setCreating(false);
      setSearchParams((prev) => stripAdminEventPrefillKeys(prev), { replace: true });
    }
    setErr(null);
    setActionMsg(null);
    try {
      if (wasCreating) {
        const baseDate = String(body.event_date ?? "").slice(0, 10);
        const repeatDates = weeklyRepeat.enabled ? weeklyRepeatDates(baseDate, weeklyRepeat.until) : [];
        if (weeklyRepeat.enabled) {
          if (repeatDates.length === 0) throw new Error("毎週繰り返しの開催日と終了日を確認してください。");
          const repeatBody = { ...body, id: "", weekly_dates: repeatDates };
          const res = await apiJson<{ ok?: boolean; id?: string; count?: number }>("/api/admin/events", { method: "POST", body: JSON.stringify(repeatBody) });
          if (keepOpen) {
            setCreating(true);
            setEditing({
              ...body,
              id: "",
              performers_json: String(body.performers_json ?? "[]"),
            });
          }
          setWeeklyRepeat({ enabled: false, until: "" });
          await reload();
          setSaveFeedback(keepOpen ? `保存しました（${Number(res.count ?? repeatDates.length)}件）。新規作成を続けられます。` : null);
          setActionMsg(`毎週繰り返し予定を${Number(res.count ?? repeatDates.length)}件保存しました。`);
          return;
        }
        await apiJson<{ ok?: boolean; id?: string }>("/api/admin/events", { method: "POST", body: JSON.stringify(body) });
        if (keepOpen) {
          setCreating(true);
          setEditing({
            ...body,
            id: "",
            performers_json: String(body.performers_json ?? "[]"),
          });
        }
        setWeeklyRepeat({ enabled: false, until: "" });
        await reload();
        setSaveFeedback(keepOpen ? "保存しました。内容はこのまま残して、新規作成を続けられます。" : null);
        setActionMsg(keepOpen ? "保存しました。続けて新規作成できます。" : "保存しました。画面を閉じました。");
        return;
      }
      await apiJson(`/api/admin/events/${String(body.id)}`, { method: "PUT", body: JSON.stringify(body) });
      if (weeklyRepeat.enabled) {
        const baseDate = String(body.event_date ?? "").slice(0, 10);
        const repeatDates = weeklyRepeatDates(baseDate, weeklyRepeat.until);
        const copyDates = repeatDates.slice(1);
        if (copyDates.length === 0) throw new Error("編集画面から毎週登録する場合は、翌週以降も含む繰り返し終了日を指定してください。");
        const repeatBody = { ...body, id: "", weekly_dates: copyDates };
        const res = await apiJson<{ ok?: boolean; id?: string; count?: number }>("/api/admin/events", { method: "POST", body: JSON.stringify(repeatBody) });
        setWeeklyRepeat({ enabled: false, until: "" });
        if (!keepOpen) {
          setEditing(null);
          setCreating(false);
          setSearchParams((prev) => stripAdminEventPrefillKeys(prev), { replace: true });
        }
        await reload();
        setSaveFeedback(keepOpen ? `保存しました。翌週以降を${Number(res.count ?? copyDates.length)}件追加しました。` : null);
        setActionMsg(`保存しました。翌週以降を${Number(res.count ?? copyDates.length)}件追加しました。`);
        return;
      }
      if (!keepOpen) {
        setEditing(null);
        setCreating(false);
        setSearchParams((prev) => stripAdminEventPrefillKeys(prev), { replace: true });
      }
      await reload();
      setSaveFeedback(keepOpen ? "保存しました。内容はこのまま残っています。" : null);
      setActionMsg(keepOpen ? "保存しました。続けて編集できます。" : "保存しました。画面を閉じました。");
    } catch (e) {
      if (!keepOpen) {
        setCreating(wasCreating);
        setEditing(body);
      }
      setSaveFeedback("保存に失敗しました。内容は残っています。");
      setErr(String(e));
    } finally {
      setSaveBusy(false);
    }
  };

  const duplicateToDate = async () => {
    if (saveBusy) return;
    const body = buildSaveBody();
    const targetDate = duplicateDate.trim();
    if (!body) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(targetDate)) {
      setErr("追加する開催日を選んでください。");
      return;
    }
    if (targetDate === String(body.event_date ?? "").slice(0, 10)) {
      setErr("元の開催日と同じ日には追加できません。別の日付を選んでください。");
      return;
    }
    setSaveBusy(true);
    setErr(null);
    setActionMsg(null);
    setSaveFeedback("別日に追加中です…");
    try {
      await apiJson<{ ok?: boolean; id?: string }>("/api/admin/events", {
        method: "POST",
        body: JSON.stringify({ ...body, id: "", event_date: targetDate }),
      });
      setDuplicateDate("");
      await reload();
      setSaveFeedback(`${targetDate} に同じ内容の予定を追加しました。`);
      setActionMsg(`${targetDate} に同じ内容の予定を追加しました。`);
    } catch (e) {
      setSaveFeedback("別日への追加に失敗しました。内容は残っています。");
      setErr(String(e));
    } finally {
      setSaveBusy(false);
    }
  };

  const del = async (id: string) => {
    if (!confirm("表サイトから非表示にしてアーカイブしますか？後から「アーカイブ一覧」で復帰できます。")) return;
    const prevRows = rows;
    setRows((p) => p.filter((r) => String(r.id) !== id));
    setActionMsg("表サイトから非表示にしてアーカイブしました。");
    try {
      await apiJson(`/api/admin/events/${id}`, { method: "DELETE" });
    } catch (e) {
      setRows(prevRows);
      setErr(String(e));
      setActionMsg("アーカイブに失敗しました。元に戻しました。");
    }
  };

  const restore = async (r: Row) => {
    const id = String(r.id);
    const targetMode = inferAdminModeForRow(r);
    await apiJson(`/api/admin/events/${id}/restore`, { method: "POST", body: JSON.stringify({ visibility: "unreleased" }) });
    setActionMsg("非公開の状態で管理画面に復帰しました。公開する場合は通常一覧で「公開にする」を押してください。");
    await reload();
    navigate(`/admin/events/${targetMode}?edit=${encodeURIComponent(id)}`);
  };

  const hidePublicArchived = async (id: string) => {
    applyVisibilityToLocalRows(id, "unreleased");
    setActionMsg("アーカイブのまま表サイトから非表示にしました。反映中です。");
    try {
      await apiJson(`/api/admin/events/${id}/visibility`, { method: "PATCH", body: JSON.stringify({ visibility: "unreleased" }) });
      setActionMsg("アーカイブのまま表サイトから非表示にしました。");
      await reload();
    } catch (e) {
      setErr(String(e));
      setActionMsg("表サイトから非表示にできませんでした。再読み込みして確認してください。");
      await reload();
    }
  };

  const permanentDelete = async (id: string, eventName: string) => {
    const label = (eventName.trim() || id).replace(/\s+/g, " ").slice(0, 120);
    if (!confirm(`「${label}」を完全に削除しますか？\nデータベースから消え、告知の投稿記録も失われます。この操作は取り消せません。`)) return;
    const prevRows = rows;
    setRows((p) => p.filter((r) => String(r.id) !== id));
    setActionMsg("完全削除しました。画面から消しました。");
    try {
      await apiJson(`/api/admin/events/${id}/permanent`, { method: "DELETE" });
    } catch (e) {
      setRows(prevRows);
      setErr(String(e));
      setActionMsg("完全削除に失敗しました。元に戻しました。");
    }
  };

  /** PATCH 成功直後に一覧・編集中フォームへ反映（await reload を待たず操作できるようにする） */
  const applyVisibilityToLocalRows = (id: string, visibility: "public" | "unreleased") => {
    setVisibilityOverride((prev) => ({ ...prev, [id]: visibility }));
    setRows((prev) =>
      prev.map((r) => {
        if (String(r.id) !== id) return r;
        if (visibility === "public") return { ...r, visibility, status: "ok" };
        return { ...r, visibility };
      }),
    );
    setEditing((e) => {
      if (!e || String(e.id) !== id) return e;
      if (visibility === "public") return { ...e, visibility, status: "ok" };
      return { ...e, visibility };
    });
  };

  const applyVisibilityToLocalRowsByIds = (ids: string[], visibility: "public" | "unreleased") => {
    const set = new Set(ids);
    setRows((prev) =>
      prev.map((r) => {
        if (!set.has(String(r.id))) return r;
        if (visibility === "public") return { ...r, visibility, status: "ok" };
        return { ...r, visibility };
      }),
    );
    setEditing((e) => {
      if (!e || !set.has(String(e.id))) return e;
      if (visibility === "public") return { ...e, visibility, status: "ok" };
      return { ...e, visibility };
    });
  };

  const drainRowVisibilityToServer = (id: string) => {
    if (visibilityDrainRunningRef.current.has(id)) return;
    visibilityDrainRunningRef.current.add(id);
    void (async () => {
      try {
        for (;;) {
          const target = visibilityDesiredRef.current[id];
          if (target === undefined) break;
          try {
            await apiJson(`/api/admin/events/${encodeURIComponent(id)}/visibility`, {
              method: "PATCH",
              body: JSON.stringify({ visibility: target }),
            });
            setErr(null);
            setActionMsg(target === "public" ? "公開にしました。" : "非公開にしました。");
          } catch (e) {
            setErr(String(e));
            delete visibilityDesiredRef.current[id];
            setVisibilityOverride((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            });
            await reload();
            break;
          }
          if (visibilityDesiredRef.current[id] === target) {
            delete visibilityDesiredRef.current[id];
            break;
          }
        }
      } finally {
        visibilityDrainRunningRef.current.delete(id);
        if (visibilityDesiredRef.current[id] !== undefined) drainRowVisibilityToServer(id);
      }
    })();
  };

  const setRowVisibility = (id: string, visibility: "public" | "unreleased") => {
    visibilityDesiredRef.current[id] = visibility;
    flushSync(() => applyVisibilityToLocalRows(id, visibility));
    setActionMsg(visibility === "public" ? "公開に切り替えました！（表サイト反映中）" : "非公開に切り替えました！（表サイト反映中）");
    drainRowVisibilityToServer(id);
  };

  const bulkPublishVisibleRows = async () => {
    if (!sortedRows.length) return;
    if (!confirm(`この一覧の ${sortedRows.length} 件をすべて「公開」にしますか？（絞り込み後の行のみ）`)) return;
    const ids = sortedRows.map((r) => String(r.id));
    const prevById = new Map(
      rows
        .filter((r) => ids.includes(String(r.id)))
        .map((r) => [
          String(r.id),
          adminVisibilityKey(r.visibility) === "public" ? ("public" as const) : ("unreleased" as const),
        ]),
    );
    flushSync(() => {
      applyVisibilityToLocalRowsByIds(ids, "public");
      setBulkPubPick({});
    });
    setVisibilitySaving(true);
    setErr(null);
    try {
      await apiJson("/api/admin/events/bulk-visibility", {
        method: "POST",
        body: JSON.stringify({ ids, visibility: "public" }),
      });
      setActionMsg(`${ids.length}件を公開にしました。`);
    } catch (e) {
      flushSync(() => {
        for (const [id, pv] of prevById) applyVisibilityToLocalRows(id, pv);
      });
      setErr(String(e));
    } finally {
      setVisibilitySaving(false);
    }
  };

  const bulkUnpublishVisibleRows = async () => {
    if (!sortedRows.length) return;
    if (
      !confirm(
        `このページに表示中の ${sortedRows.length} 件をすべて「非公開」にしますか？（絞り込み後の行のみ。表サイトの予定から外れます）`,
      )
    )
      return;
    const ids = sortedRows.map((r) => String(r.id));
    const prevById = new Map(
      rows
        .filter((r) => ids.includes(String(r.id)))
        .map((r) => [
          String(r.id),
          adminVisibilityKey(r.visibility) === "public" ? ("public" as const) : ("unreleased" as const),
        ]),
    );
    flushSync(() => {
      applyVisibilityToLocalRowsByIds(ids, "unreleased");
      setBulkPubPick({});
    });
    setVisibilitySaving(true);
    setErr(null);
    try {
      await apiJson("/api/admin/events/bulk-visibility", {
        method: "POST",
        body: JSON.stringify({ ids, visibility: "unreleased" }),
      });
      setActionMsg(`${ids.length}件を非公開にしました。`);
    } catch (e) {
      flushSync(() => {
        for (const [id, pv] of prevById) applyVisibilityToLocalRows(id, pv);
      });
      setErr(String(e));
    } finally {
      setVisibilitySaving(false);
    }
  };

  const bulkPublishPickedRows = async () => {
    const ids = unreleasedIdsInSorted.filter((id) => bulkPubPick[id]);
    if (!ids.length) return;
    if (!confirm(`チェックした非公開の ${ids.length} 件を「公開」にしますか？`)) return;
    const prevById = new Map(
      rows
        .filter((r) => ids.includes(String(r.id)))
        .map((r) => [
          String(r.id),
          adminVisibilityKey(r.visibility) === "public" ? ("public" as const) : ("unreleased" as const),
        ]),
    );
    flushSync(() => {
      applyVisibilityToLocalRowsByIds(ids, "public");
      setBulkPubPick({});
    });
    setVisibilitySaving(true);
    setErr(null);
    try {
      await apiJson("/api/admin/events/bulk-visibility", {
        method: "POST",
        body: JSON.stringify({ ids, visibility: "public" }),
      });
      setActionMsg(`${ids.length}件を公開にしました。`);
    } catch (e) {
      flushSync(() => {
        for (const [id, pv] of prevById) applyVisibilityToLocalRows(id, pv);
      });
      setErr(String(e));
    } finally {
      setVisibilitySaving(false);
    }
  };

  const togglePickAllUnreleased = () => {
    if (allUnreleasedPicked) {
      setBulkPubPick((p) => {
        const next = { ...p };
        for (const id of unreleasedIdsInSorted) delete next[id];
        return next;
      });
    } else {
      setBulkPubPick((p) => {
        const next = { ...p };
        for (const id of unreleasedIdsInSorted) next[id] = true;
        return next;
      });
    }
  };

  const dismissForm = () => {
    setEditing(null);
    setCreating(false);
    setWeeklyRepeat({ enabled: false, until: "" });
    setDuplicateDate("");
    setSaveFeedback(null);
  };

  useEffect(() => {
    if (!creating) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") dismissForm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [creating]);

  const todayYmdJp = todayJp();

  return (
    <>
      <header className="topbar admin-events-page-topbar">
        <div className="brand">
          <h1>{title}</h1>
          <p className="mono">
            {sortedRows.length} 件表示
            {mode === "jp_room" && filtered.length > filteredDeduped.length
              ? `（同一告知の重複 ${filtered.length - filteredDeduped.length} 行は1件にまとめて表示）`
              : null}
            {(adminEventDateFrom || adminEventDateTo || adminKeyword || adminVis || adminKarinSubtype !== "all") &&
              modeFiltered.length !== filtered.length
              ? `（開催日・キーワード・公開状態の絞り前 ${modeFiltered.length} 件）`
              : null}
          </p>
          <p className="admin-events-calendar-notice-hint">
            表サイト・カレンダー上の「まもなく締切・まもなく開催」「本日開催」などは、この一覧の開催月だけでは見えないことがあります。{" "}
            <Link className="admin-inline-link" to="/admin/announcements">
              お知らせ管理
            </Link>
            の「カレンダー上のお知らせ」と同じ内容で確認できます。
          </p>
        </div>
        <div className="row">
          <button className="btn small" type="button" onClick={openCreate}>
            ＋ 新規
          </button>
          {mode !== "archive" ? (
            <>
              <button
                className="btn small secondary"
                type="button"
                disabled={visibilitySaving || pickedUnreleasedCount === 0}
                onClick={() => void bulkPublishPickedRows()}
              >
                選択を公開（{pickedUnreleasedCount}件）
              </button>
              <button
                className="btn small secondary"
                type="button"
                disabled={visibilitySaving || sortedRows.length === 0}
                onClick={() => void bulkPublishVisibleRows()}
              >
                全て公開（この一覧）
              </button>
              <button
                className="btn small secondary"
                type="button"
                disabled={visibilitySaving || sortedRows.length === 0}
                onClick={() => void bulkUnpublishVisibleRows()}
              >
                全て非公開（このページ）
              </button>
            </>
          ) : null}
        </div>
      </header>

      {actionMsg && (
        <p className="admin-action-toast" role="status">
          {actionMsg}
        </p>
      )}
      {fetchError ? <p style={{ color: "#b00020" }}>{fetchError}</p> : null}
      {err && <p style={{ color: "#b00020" }}>{err}</p>}

      <details className="admin-vis-legend-details">
        <summary>表サイトの色・絞り込みの説明</summary>
        <p className="admin-vis-legend">
          <strong>表サイト</strong>：左端の色＝<span style={{ color: "#2e7d32" }}>緑</span>は公開中／<span style={{ color: "#5e35b1" }}>紫</span>は非公開。非公開の行は<strong>薄いグレー背景</strong>。トグルは「公開」＝非公開中は白・公開中は<strong style={{ color: "#2e7d32" }}>緑の塗りつぶし</strong>、「非公開」選択中は紫グラデです。
          {mode !== "archive" ? (
            <>
              {" "}
              先頭の<strong>チェック</strong>は非公開行のみ。選んで<strong>選択を公開</strong>でまとめて公開できます。
              <strong>全て公開／全て非公開</strong>は、このページに表示中の行（絞り込み後）すべてが対象です。
            </>
          ) : null}
          {mode !== "archive" ? (
            <>
              {" "}
              <strong>開催日</strong>は、絞り込みで日付・月を空にすると<strong>今月（日本時間）</strong>が入り、その範囲で表示します。加えて<strong>2026年5月1日より前</strong>の開催はこの一覧には出しません（DB には残っています）。
            </>
          ) : null}{" "}
          <strong>開催日が今日（日本時間）のセルは赤背景</strong>です。
        </p>
      </details>

      {(creating || editing) && editing
        ? createPortal(
            <div className="admin-event-modal-root" role="presentation" onClick={() => dismissForm()}>
              <div
                className="admin-event-modal-panel card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="admin-event-modal-title"
                onClick={(ev) => ev.stopPropagation()}
              >
                <InlineEventForm
                  creating={creating}
                  editing={editing}
                  setEditing={setEditing}
                  perfSelection={perfSelection}
                  setPerfSelection={setPerfSelection}
                  weeklyRepeat={weeklyRepeat}
                  setWeeklyRepeat={setWeeklyRepeat}
                  duplicateDate={duplicateDate}
                  setDuplicateDate={setDuplicateDate}
                  saveBusy={saveBusy}
                  saveFeedback={saveFeedback}
                  onDuplicateDate={() => void duplicateToDate()}
                  onSaveClose={() => void save(false)}
                  onSaveKeep={() => void save(true)}
                  onCancel={dismissForm}
                />
              </div>
            </div>,
            document.body,
          )
        : null}

      <div id={ADMIN_EVENTS_LIST_SCROLL_ID} className="admin-events-list-scroll card admin-events-table-wrap">
        <table className="table admin-events-table">
          <thead>
            <tr>
              {mode !== "archive" ? (
                <th className="admin-events-bulk-pick-th" title="非公開行をまとめて選ぶ（ツールバーの「選択を公開」用）">
                  <div className="admin-events-bulk-pick-th-inner">
                    <input
                      ref={bulkPickHeaderRef}
                      type="checkbox"
                      className="admin-events-bulk-pick-cb admin-events-bulk-pick-cb--header"
                      checked={allUnreleasedPicked}
                      disabled={visibilitySaving || unreleasedIdsInSorted.length === 0}
                      onChange={() => togglePickAllUnreleased()}
                      aria-label="表示中の非公開行をすべて選択"
                    />
                    <span className="admin-events-bulk-pick-th-caption">選択</span>
                  </div>
                </th>
              ) : null}
              <th title="取り込み元と pending など（行は「開く」で表示）">ソース・状態</th>
              <th title="events.event_date">開催日</th>
              <th className="admin-th-wrap admin-th-showtime" title="performance_text など">
                本番・
                <br />
                時間
              </th>
              <th className="admin-th-wrap" title="event_name">
                イベント名
              </th>
              <th title="venue">会場</th>
              <th
                title="reception_period_text / reception_start〜reception_end（長い発売日メモなど）"
                className="admin-th-reserve"
              >
                予約期間
              </th>
              <th title="detail_url（公式詳細）">URL</th>
              <th title="homepage_public_date / homepage_public_at">HP公開</th>
              <th title="visibility（表サイトに載るか）">表サイト</th>
              {mode === "archive" ? (
                <th className="mono" title="deleted_at">
                  アーカイブ
                </th>
              ) : null}
              <th className="admin-events-th-actions">操作</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => {
              const vis = visibilityUi(r.visibility);
              const vk = adminVisibilityKey(r.visibility);
              const evYmd = rowEventDateIsoDate(r);
              const isEventDateToday = evYmd !== "" && evYmd === todayYmdJp;
              const isFocusDay = Boolean(adminFocusDay && evYmd === adminFocusDay);
              const visIsPublic = vk === "public";
              const visIsUnreleased = vk === "unreleased";
              const visStrongLabel = visIsPublic ? "公開中" : visIsUnreleased ? "非公開中" : "未設定";
              const visStrongStyle = visIsPublic
                ? {
                    background: "#1b5e20",
                    border: "3px solid #0d3d12",
                    color: "#fff",
                  }
                : visIsUnreleased
                  ? {
                      background: "#5e35b1",
                      border: "3px solid #311b92",
                      color: "#fff",
                    }
                  : {
                      background: "#607d8b",
                      border: "3px solid #455a64",
                      color: "#fff",
                    };
              const rowInlineStyle = visIsPublic
                ? { boxShadow: "inset 8px 0 0 #2e7d32" }
                : visIsUnreleased
                  ? { boxShadow: "inset 8px 0 0 #5e35b1" }
                  : undefined;
              return (
              <Fragment key={String(r.id)}>
                <tr
                  className={`${vis.rowClass}${isFocusDay ? " admin-row--focused" : ""}`}
                  style={rowInlineStyle}
                  data-admin-row-date={evYmd || undefined}
                >
                  {mode !== "archive" ? (
                    <td
                      className="admin-events-bulk-pick-td"
                      data-label="一括で公開へ"
                      style={{ verticalAlign: "top", textAlign: "center" }}
                      title="非公開の行だけチェックできます。ツールバーの「選択を公開」でまとめて公開します。"
                    >
                      {vk === "unreleased" ? (
                        <span className="admin-events-bulk-pick-hit">
                          <input
                            type="checkbox"
                            className="admin-events-bulk-pick-cb"
                            checked={!!bulkPubPick[String(r.id)]}
                            disabled={visibilitySaving}
                            onChange={(e) => {
                              const id = String(r.id);
                              setBulkPubPick((p) => ({ ...p, [id]: e.target.checked }));
                            }}
                            aria-label={`「${String(r.event_name ?? "").replace(/\s+/g, " ").trim().slice(0, 80)}」を一括公開の対象にする`}
                          />
                        </span>
                      ) : (
                        <span className="admin-events-bulk-pick-hit admin-events-bulk-pick-hit--muted" aria-hidden>
                          <span className="mono" style={{ color: "var(--muted)", fontSize: 12 }}>
                            —
                          </span>
                        </span>
                      )}
                    </td>
                  ) : null}
                  <td data-label="詳細" style={{ verticalAlign: "top" }}>
                    <details className="admin-source-status-details">
                      <summary>開く</summary>
                      <div className="mono" style={{ marginTop: 6, fontSize: 11, lineHeight: 1.5 }}>
                        <div>
                          <span style={{ color: "var(--muted)" }}>ソース</span> {String(r.source ?? "").trim() || "—"}
                        </div>
                        <div>
                          <span style={{ color: "var(--muted)" }}>状態</span> {String(r.status ?? "").trim() || "—"}
                        </div>
                        {r.hp_schedule_category != null && String(r.hp_schedule_category).trim() !== "" ? (
                          <div>
                            <span style={{ color: "var(--muted)" }}>HP区分</span> {String(r.hp_schedule_category)}
                          </div>
                        ) : null}
                        {r.ingest_trust_score != null && String(r.ingest_trust_score).trim() !== "" ? (
                          <div>
                            <span style={{ color: "var(--muted)" }}>取込信頼度</span> {String(r.ingest_trust_score)}
                          </div>
                        ) : null}
                        {r.ingest_last_fetch_at != null && String(r.ingest_last_fetch_at).trim() !== "" ? (
                          <div>
                            <span style={{ color: "var(--muted)" }}>取込時刻</span> {String(r.ingest_last_fetch_at)}
                          </div>
                        ) : null}
                        {r.ingest_excerpt != null && String(r.ingest_excerpt).trim() !== "" ? (
                          <div style={{ marginTop: 4 }}>
                            <span style={{ color: "var(--muted)" }}>抜粋</span> {previewCell(r.ingest_excerpt, 120)}
                          </div>
                        ) : null}
                        {r.ingest_flags != null && String(r.ingest_flags).trim() !== "" ? (
                          <div style={{ marginTop: 4, wordBreak: "break-word" }}>
                            <span style={{ color: "var(--muted)" }}>取込flags</span> {previewCell(r.ingest_flags, 160)}
                          </div>
                        ) : null}
                      </div>
                    </details>
                  </td>
                  <td
                    className={isEventDateToday ? "mono admin-event-date-is-today" : "mono"}
                    data-label="開催日"
                    style={{ fontSize: 11 }}
                    title="events.event_date（一覧の並び・表サイトの日付キー）"
                  >
                    {String(r.event_date ?? "").trim() || "—"}
                  </td>
                  <td data-label="本番・時間" style={{ maxWidth: 220, fontSize: 11 }} title={String(r.performance_text ?? schedulePreviewCell(r) ?? "")}>
                    {previewCell(schedulePreviewCell(r), 80)}
                  </td>
                  <td data-label="イベント名">
                    <span className="admin-vis-event-prefix" style={visStrongStyle}>
                      {visStrongLabel}
                    </span>
                    <span>{String(r.event_name)}</span>
                  </td>
                  <td data-label="会場">{String(r.venue ?? "")}</td>
                  <td data-label="予約期間" style={{ maxWidth: 180, fontSize: 11 }} title={String(r.reception_period_text ?? "")}>
                    {previewCell(r.reception_period_text ?? r.reception_start, 72)}
                  </td>
                  <td data-label="URL" style={{ maxWidth: 140, fontSize: 11 }}>
                    {r.detail_url ? (
                      <a className="mono" href={String(r.detail_url)} target="_blank" rel="noreferrer">
                        開く
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="mono" data-label="HP公開" title={String(r.homepage_public_at ?? "").trim() || undefined}>
                    {formatHomepagePublicCell(r) || "—"}
                  </td>
                  <td data-label="表サイト" className="admin-vis-status-cell" style={{ fontSize: 11, verticalAlign: "top" }}>
                    <div className="admin-vis-strong-panel" style={visStrongStyle}>
                      {visStrongLabel}
                    </div>
                    <span className={vis.badgeClass} title={`DB: visibility=${vis.raw}`}>
                      {vis.label}
                    </span>
                    <div className={`admin-vis-now admin-vis-now--${vk}`}>
                      {vk === "public" ? "現在：公開中" : vk === "unreleased" ? "現在：非公開" : "現在：未設定"}
                    </div>
                    <div className="mono admin-vis-raw">
                      DB: {vis.raw || "—"}
                    </div>
                    {mode !== "archive" ? (
                      <>
                        <div className="row admin-vis-toggle-row">
                          <button
                            className={`btn small table-action admin-vis-toggle-public${
                              vk === "public" ? " admin-vis-toggle-public--active" : " admin-vis-toggle-public--idle"
                            }`}
                            type="button"
                            disabled={vk === "public"}
                            onClick={() => void setRowVisibility(String(r.id), "public")}
                          >
                            {vk === "public" ? "公開中" : "公開にする"}
                          </button>
                          <button
                            className={`btn small secondary table-action${vk === "unreleased" ? " admin-vis-toggle-unreleased--active" : ""}`}
                            type="button"
                            disabled={vk === "unreleased"}
                            onClick={() => void setRowVisibility(String(r.id), "unreleased")}
                          >
                            {vk === "unreleased" ? "非公開中" : "非公開にする"}
                          </button>
                        </div>
                      </>
                    ) : null}
                  </td>
                  {mode === "archive" ? <td className="mono" data-label="アーカイブ" style={{ fontSize: 11 }}>{String(r.deleted_at ?? "").trim() || "—"}</td> : null}
                  <td className="table-actions" data-label="操作">
                    {mode === "archive" ? (
                      <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
                        <button className="btn small table-action" type="button" onClick={() => void restore(r)}>
                          非公開で復帰
                        </button>
                        <button
                          className="btn small secondary table-action"
                          type="button"
                          disabled={vk === "unreleased"}
                          onClick={() => void hidePublicArchived(String(r.id))}
                        >
                          {vk === "unreleased" ? "表サイト非表示済み" : "表サイトから非表示"}
                        </button>
                        <button
                          className="btn small secondary table-action"
                          type="button"
                          style={{ color: "#8b0000", borderColor: "rgba(139,0,0,0.35)" }}
                          onClick={() => void permanentDelete(String(r.id), String(r.event_name ?? ""))}
                        >
                          完全削除
                        </button>
                      </div>
                    ) : (
                      <>
                        <button className="btn small secondary table-action" type="button" onClick={() => openEdit(r)}>
                          編集
                        </button>
                        <button className="btn small secondary table-action" type="button" onClick={() => void del(String(r.id))}>
                          表サイトから非表示にしてアーカイブ
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              </Fragment>
            );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
